import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Layout,
  Select,
  Spinner,
  Text,
  TextField,
  Badge,
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18n.js';
import { api } from '../lib/api.js';
import type { BillingStatus, ShopSettings } from '../lib/types.js';
import { AppPage } from '../components/Layout.js';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n.js';
import { getAppBridge } from '../lib/app-bridge.js';

const LANG_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ja: '日本語',
  'pt-BR': 'Português (Brasil)',
  'zh-CN': '中文（简体）',
};

const SENSITIVITY_OPTIONS = [
  { label: 'settings.sensitivityStandard', value: 'standard' },
  { label: 'settings.sensitivityStrict', value: 'strict' },
  { label: 'settings.sensitivityLoose', value: 'loose' },
];

const LANGUAGE_SECTION_ID = 'language-settings';

function scrollToLanguageSection(): void {
  const el = document.getElementById(LANGUAGE_SECTION_ID);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Settings(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields backed by local state; flush to server on Save.
  const [badgeEnabled, setBadgeEnabled] = useState(false);
  const [alertEmail, setAlertEmail] = useState('');
  const [language, setLanguage] = useState<SupportedLanguage>('en');

  // Disclosure text is no longer merchant-editable — it's a translation keyed
  // off the selected store language so regulators and shoppers see consistent
  // wording across every shop running the app. We preview in the language the
  // merchant is currently picking (not i18n's active locale) so the TextField
  // updates live when they change the Language dropdown.
  const disclosureText = t('settings.defaultDisclosureValue', { lng: language });

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const [s, b] = await Promise.all([
          api.get<{ settings: ShopSettings }>('/api/settings', ac.signal),
          api.get<BillingStatus>('/api/billing/status', ac.signal),
        ]);
        setSettings(s.settings);
        setBilling(b);
        setBadgeEnabled(s.settings.badgeEnabled);
        setAlertEmail(s.settings.alertEmail ?? '');
        setLanguage(s.settings.language);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  // Deep-link from BadgeSettings "Update language" or a hash landing: once
  // the form has rendered (after loading resolves), scroll the language
  // Select into view. Scrolling before mount is a no-op — the element
  // doesn't exist yet — so this intentionally runs on `loading` change.
  useEffect(() => {
    if (loading) return;
    const state = location.state as { scrollTo?: string } | null;
    const wantsScroll = state?.scrollTo === 'language' || window.location.hash === `#${LANGUAGE_SECTION_ID}`;
    if (wantsScroll) scrollToLanguageSection();
  }, [loading, location.state]);

  const save = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        disclosureText,
        badgeEnabled,
        language,
      };
      if (alertEmail) body.alertEmail = alertEmail;
      else body.alertEmail = null;

      const res = await api.put<{ settings: ShopSettings }>('/api/settings', body);
      setSettings(res.settings);
      setSaved(true);
      void i18n.changeLanguage(language);
      const app = await getAppBridge();
      app.toast.show(t('settings.saved'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [disclosureText, badgeEnabled, alertEmail, language, t]);

  if (loading) {
    return (
      <AppPage
        title={t('settings.title')}
        backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}
      >
        <InlineStack align="center">
          <Spinner accessibilityLabel="Loading" size="large" />
        </InlineStack>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={t('settings.title')}
      backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}
      primaryAction={{ content: t('settings.save'), onAction: save, loading: saving }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error ? (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            ) : null}
            {saved ? (
              <Banner tone="success" onDismiss={() => setSaved(false)}>
                {t('settings.saved')}
              </Banner>
            ) : null}

            {/* Disclosure label defaults */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t('settings.disclosureDefaultsTitle')}
                </Text>
                <TextField
                  label={t('settings.defaultText')}
                  value={disclosureText}
                  onChange={() => {}}
                  disabled
                  autoComplete="off"
                  helpText={
                    <span>
                      {t('settings.defaultDisclosureHelp')}{' '}
                      <Button variant="plain" onClick={scrollToLanguageSection}>
                        {t('settings.updateLanguage')}
                      </Button>
                    </span>
                  }
                />
                <Select
                  label={t('settings.insertionMethod')}
                  options={[{ label: t('settings.methodAppend'), value: 'append' }]}
                  value="append"
                  onChange={() => {}}
                />
              </BlockStack>
            </Card>

            {/* Scan settings */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t('settings.scanSettingsTitle')}
                </Text>
                <Select
                  label={t('settings.sensitivity')}
                  options={SENSITIVITY_OPTIONS.map((o) => ({
                    label: t(o.label),
                    value: o.value,
                  }))}
                  value="standard"
                  onChange={() => {}}
                />
                <Checkbox
                  label={t('settings.scanDescriptions')}
                  checked={true}
                  onChange={() => {}}
                />
                <Checkbox label={t('settings.scanMetas')} checked={true} onChange={() => {}} />
                <Checkbox
                  label={t('settings.scanImageMetadata')}
                  checked={true}
                  onChange={() => {}}
                />
                <Checkbox label={t('settings.scanImageAlt')} checked={false} onChange={() => {}} />
              </BlockStack>
            </Card>

            {/* Selling regions */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t('settings.regionsTitle')}
                </Text>
                <Text as="p" tone="subdued">
                  {t('settings.regionsBody')}
                </Text>
                <RegionRow region={t('settings.regionEU')} tag={t('settings.tagEU')} tone="warning" />
                <RegionRow region={t('settings.regionUS')} tag={t('settings.tagFTC')} tone="critical" />
                <RegionRow region={t('settings.regionNY')} tag={t('settings.tagNY')} tone="info" />
              </BlockStack>
            </Card>

            {/* Language */}
            <div id={LANGUAGE_SECTION_ID}>
              <Card>
                <Select
                  label={t('settings.language')}
                  options={SUPPORTED_LANGUAGES.map((code) => ({
                    label: LANG_LABELS[code],
                    value: code,
                  }))}
                  value={language}
                  onChange={(v) => setLanguage(v as SupportedLanguage)}
                />
              </Card>
            </div>

            {/* Notifications */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t('settings.notificationsTitle')}
                </Text>
                <TextField
                  label={t('settings.alertEmail')}
                  type="email"
                  value={alertEmail}
                  onChange={(v) => setAlertEmail(v)}
                  autoComplete="email"
                  helpText={t('settings.alertEmailHint')}
                  placeholder="owner@example.com"
                />
              </BlockStack>
            </Card>

            {/* Compliance badge entry */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    {t('settings.badgeEntryTitle')}
                  </Text>
                  {billing?.plan === 'monitoring' ? (
                    <Badge tone={badgeEnabled ? 'success' : 'info'}>
                      {badgeEnabled
                        ? t('settings.badgeEntryEnabled')
                        : t('settings.badgeEntryDisabled')}
                    </Badge>
                  ) : (
                    <Badge tone="attention">{t('settings.badgeEntryMonitoringOnly')}</Badge>
                  )}
                </InlineStack>
                <Text as="p" tone="subdued">
                  {t('settings.badgeEntryBody')}
                </Text>
                <InlineStack>
                  <Button onClick={() => navigate('/badge')}>
                    {t('settings.badgeEntryCta')}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>

            {/* Billing */}
            {billing ? (
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      {t('settings.billingTitle')}
                    </Text>
                    <Badge tone={billing.plan === 'free' ? 'info' : 'success'}>
                      {t(`plans.tierName.${billing.plan}`)}
                    </Badge>
                  </InlineStack>
                  <Text as="p" tone="subdued">
                    {t('plans.usageSummary', {
                      used: billing.usageCount,
                      limit: billing.usageLimit,
                    })}
                  </Text>
                  <Button onClick={() => navigate('/plans')}>
                    {t('plans.viewPlans')}
                  </Button>
                </BlockStack>
              </Card>
            ) : null}

            <InlineStack align="end">
              <Button variant="primary" loading={saving} onClick={save}>
                {t('settings.save')}
              </Button>
            </InlineStack>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </AppPage>
  );
}

function RegionRow({
  region,
  tag,
  tone,
}: {
  region: string;
  tag: string;
  tone: 'warning' | 'critical' | 'info';
}): JSX.Element {
  return (
    <InlineStack align="space-between" blockAlign="center">
      <Text as="span">{region}</Text>
      <Badge tone={tone}>{tag}</Badge>
    </InlineStack>
  );
}
